import React from 'react';
import { Info, Minus, Plus, Save } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card as UiCard } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { FormField } from '@/components/app';

/**
 * Общие поля формы для вкладок раздела «Игры»: спарринг и лимиты правятся
 * одинаковыми контролами, дублировать их по вкладкам незачем.
 */
export function Card({
  title,
  icon,
  children,
  dimmed,
  hint,
  className,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  dimmed?: boolean;
  /** Пояснение по наведению на ⓘ у заголовка. */
  hint?: string;
  className?: string;
}) {
  return (
    <UiCard className={cn('flex flex-col gap-4 p-[18px]', dimmed && 'opacity-60', className)}>
      <div className="flex items-center gap-3">
        {icon && (
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-muted text-text [&_svg]:size-[18px]" aria-hidden>
            {icon}
          </span>
        )}
        <h2 className="m-0 font-display text-[17px] font-bold leading-tight">
          {title}
          {hint && <InfoHint text={hint} />}
        </h2>
      </div>
      {children}
    </UiCard>
  );
}

export function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <FormField label={label}>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </FormField>
  );
}

export function NumberField({
  label,
  hint,
  value,
  min,
  max,
  onChange,
  error,
}: {
  label: string;
  hint?: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  /** Текст ошибки валидации — показывается вместо подсказки. */
  error?: string | null;
}) {
  return (
    <FormField label={label} hint={error ? undefined : hint} error={error || undefined}>
      <Input
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        className="tabular-nums"
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isNaN(next)) return;
          onChange(next);
        }}
      />
    </FormField>
  );
}

/**
 * Целое число со степпером «− [n] +» и диапазоном справа — по макету «Лимиты».
 * Подсказка и ошибка связаны с полем через aria-describedby.
 */
export function StepperField({
  id,
  label,
  shortLabel,
  value,
  min,
  max,
  onChange,
  error,
  help,
  changedFrom,
  layout = 'card',
}: {
  id: string;
  label: string;
  /** Для aria-label кнопок: «Уменьшить: дуэль». */
  shortLabel: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  error?: string | null;
  help: React.ReactNode;
  /** Прежнее (сохранённое) значение, если поле изменено. */
  changedFrom?: number | null;
  /** inline — для выделенной карточки: подпись слева, степпер справа. */
  layout?: 'card' | 'inline';
}) {
  const errId = `${id}-err`;
  const helpId = `${id}-help`;
  const rangeId = `${id}-range`;
  const step = (delta: number) => {
    const base = Number.isFinite(value) ? value : min;
    onChange(Math.min(max, Math.max(min, Math.round(base) + delta)));
  };

  const stepper = (
    <div className="flex items-center gap-1.5">
      <Button type="button" variant="secondary" size="icon" aria-label={`Уменьшить: ${shortLabel}`} disabled={value <= min} onClick={() => step(-1)}>
        <Minus aria-hidden />
      </Button>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={Number.isFinite(value) ? value : ''}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errId : null, helpId, rangeId].filter(Boolean).join(' ')}
        onChange={(e) => {
          const next = e.target.value === '' ? NaN : Number(e.target.value);
          onChange(next);
        }}
        className="w-[84px] px-2.5 text-center font-display text-lg font-bold tabular-nums aria-invalid:bg-neg-soft"
      />
      <Button type="button" variant="secondary" size="icon" aria-label={`Увеличить: ${shortLabel}`} disabled={value >= max} onClick={() => step(1)}>
        <Plus aria-hidden />
      </Button>
      <span id={rangeId} className={cn('text-xs tabular-nums text-text-2', layout === 'card' ? 'ml-auto' : 'ml-1.5')}>
        от {min} до {max}
      </span>
    </div>
  );

  const changed =
    changedFrom != null ? (
      <span className="text-xs font-bold text-warn">Изменено · было {changedFrom}</span>
    ) : null;
  const errorEl = error ? (
    <span id={errId} role="alert" className="text-[13px] font-semibold text-neg">
      {error}
    </span>
  ) : null;

  if (layout === 'inline') {
    return (
      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="flex flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <label htmlFor={id} className="font-display text-[17px] font-bold">{label}</label>
            {changed}
          </span>
          <span id={helpId} className="text-text-2">{help}</span>
          {errorEl}
        </div>
        {stepper}
      </div>
    );
  }

  return (
    <UiCard className="flex flex-col gap-2.5 px-[18px] py-4">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="font-semibold">{label}</label>
        {changed}
      </div>
      {stepper}
      {errorEl}
      <span id={helpId} className="text-[13px] text-text-2">{help}</span>
    </UiCard>
  );
}

export function Toggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  const id = React.useId();
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex h-10 items-center justify-between gap-3 rounded-xl border border-border bg-surface px-3">
        <label htmlFor={id} className="text-sm font-semibold">{label}</label>
        <Switch id={id} checked={value} onCheckedChange={onChange} aria-describedby={hint ? `${id}-hint` : undefined} />
      </div>
      {hint && <span id={`${id}-hint`} className="text-xs text-text-2">{hint}</span>}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  tooltip,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  /** Короткая подпись под числом. */
  hint?: string;
  /** Развёрнутое пояснение по наведению — как читать эту метрику. */
  tooltip?: string;
  tone?: 'neutral' | 'pos' | 'neg';
}) {
  return (
    <div className="rounded-xl bg-surface-alt px-4 py-3">
      <div className="text-xs text-text-2">
        {label}
        {tooltip && <InfoHint text={tooltip} />}
      </div>
      <div className={cn('font-display text-lg font-bold tabular-nums', tone === 'pos' ? 'text-pos' : tone === 'neg' ? 'text-neg' : 'text-text')}>
        {value}
      </div>
      {hint && <div className="text-xs text-text-2">{hint}</div>}
    </div>
  );
}

export function SaveButton({
  saving,
  onClick,
  disabled,
}: {
  saving: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button type="button" onClick={onClick} disabled={saving || disabled} className="self-start">
      <Save aria-hidden />
      {saving ? 'Сохраняю…' : 'Сохранить'}
    </Button>
  );
}

/**
 * Подсказка у метрики или настройки. Своя, а не Radix: тултипу нужен
 * провайдер на всё приложение, а здесь достаточно hover без порталов.
 * `title` оставлен как запасной путь для тач-устройств.
 */
export function InfoHint({ text }: { text: string }) {
  return (
    <span className="group relative ml-1.5 inline-flex align-middle" tabIndex={0} title={text} role="img" aria-label={text}>
      <Info className="size-3.5 cursor-help text-text-3 transition-colors group-hover:text-text-2" aria-hidden />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-60 -translate-x-1/2',
          'rounded-xl border border-border bg-surface px-3 py-2 text-left text-xs font-normal normal-case leading-snug tracking-normal text-text shadow-pop',
          'invisible opacity-0 transition duration-150 group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100',
        )}
      >
        {text}
      </span>
    </span>
  );
}

/**
 * Шапка вкладки: чем этот раздел управляет и как читать цифры.
 * Нужна, чтобы админ, зашедший впервые, не гадал по названиям колонок.
 */
export function TabIntro({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'warning';
}) {
  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-sm leading-relaxed',
        tone === 'warning' ? 'bg-warn-soft text-text' : 'bg-muted text-text-2',
      )}
    >
      <Info className={cn('mt-0.5 size-4 shrink-0', tone === 'warning' ? 'text-warn' : 'text-text-2')} aria-hidden />
      <div>{children}</div>
    </div>
  );
}
